import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Area, AreaChart, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import {
  ArrowLeft, Loader2, RefreshCw, Download, TrendingUp, Wallet, Users, Percent,
  CalendarDays, ChevronLeft, ChevronRight, Coins, Undo2, SlidersHorizontal, List, ChevronDown, Info,
} from "lucide-react";
import api, { formatApiError, downloadCsv } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { RoleBadge, StatusBadge } from "@/components/Badges";
import { getExchange } from "@/data/exchanges";
import { PositionsHeatmap } from "@/components/PositionsHeatmap";
import { fmtUsd, fmtPct, fmtNum, pnlColor, fmtDate } from "@/lib/format";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarDays as CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";

const RANGES = [{ l: "7D", d: 7 }, { l: "30D", d: 30 }, { l: "90D", d: 90 }, { l: "All", d: 365 }];
const PAGE_SIZE = 10;

const StatBox = ({ icon: Icon, label, value, valueClass, testid }) => (
  <div className="rounded-md border border-[#262626] bg-[#141414] px-4 py-3.5" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="text-[10px] uppercase tracking-wide text-[#737373]">{label}</span>
      <Icon className="w-3.5 h-3.5 text-[#D4AF37]" />
    </div>
    <div className={cn("font-mono text-xl font-semibold mt-2", valueClass || "text-[#F5F5F5]")}>{value}</div>
  </div>
);

function PnlChart({ id, base = "/portfolio" }) {
  const [range, setRange] = useState(30);
  const [mode, setMode] = useState("pct");
  const [dates, setDates] = useState(undefined);
  const custom = dates?.from && dates?.to;
  const fromStr = custom ? dates.from.toISOString().slice(0, 10) : "";
  const toStr = custom ? dates.to.toISOString().slice(0, 10) : "";
  const params = custom ? { from: fromStr, to: toStr, size: 1, page: 1 } : { days: range, size: 1, page: 1 };
  const { data, isLoading } = useQuery({
    queryKey: ["pnl-chart", base, id, range, fromStr, toStr],
    queryFn: async () => (await api.get(`${base}/${id}/pnl-history`, { params })).data,
  });
  const series = (data?.series || []).map((s) => ({ date: s.date, value: mode === "pct" ? s.value : s.usd }));
  const positive = series.length && series[series.length - 1].value >= 0;
  const color = positive ? "#10B981" : "#EF4444";

  return (
    <div className="rounded-md border border-[#262626] bg-[#141414] p-4" data-testid="pnl-chart">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-[#D4AF37]" />
          <span className="font-heading font-semibold text-[#F5F5F5]">Cumulative PNL</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Popover>
            <PopoverTrigger asChild>
              <button data-testid="pnl-datepicker"
                className={cn("flex items-center gap-2 bg-[#0A0A0A] border rounded-sm px-3 py-1.5 text-xs transition-colors",
                  custom ? "border-[#D4AF37] text-[#D4AF37]" : "border-[#333333] text-[#A3A3A3] hover:border-[#D4AF37]")}>
                <CalendarIcon className="w-3.5 h-3.5" />
                {custom ? `${fromStr} → ${toStr}` : "Pick dates"}
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-auto p-0 bg-[#141414] border-[#262626]">
              <Calendar mode="range" selected={dates} onSelect={setDates} numberOfMonths={2} className="text-[#F5F5F5]" />
              {custom && (
                <div className="p-2 border-t border-[#262626] flex justify-end">
                  <button onClick={() => setDates(undefined)} data-testid="pnl-datepicker-clear" className="text-xs text-[#D4AF37] hover:underline">Clear</button>
                </div>
              )}
            </PopoverContent>
          </Popover>
          <div className="flex items-center bg-[#0A0A0A] border border-[#333333] rounded-sm p-0.5">
            {["pct", "usd"].map((m) => (
              <button key={m} onClick={() => setMode(m)} data-testid={`pnl-mode-${m}`}
                className={cn("px-2.5 py-1 text-xs rounded-sm transition-colors", mode === m ? "bg-[#D4AF37] text-[#0A0A0A] font-medium" : "text-[#A3A3A3] hover:text-[#F5F5F5]")}>
                {m === "pct" ? "%" : "$"}
              </button>
            ))}
          </div>
          <div className="flex items-center bg-[#0A0A0A] border border-[#333333] rounded-sm p-0.5">
            {RANGES.map((r) => (
              <button key={r.l} onClick={() => { setDates(undefined); setRange(r.d); }} data-testid={`pnl-range-${r.l}`}
                className={cn("px-2.5 py-1 text-xs rounded-sm transition-colors", !custom && range === r.d ? "bg-[#D4AF37] text-[#0A0A0A] font-medium" : "text-[#A3A3A3] hover:text-[#F5F5F5]")}>
                {r.l}
              </button>
            ))}
          </div>
        </div>
      </div>
      {isLoading ? (
        <div className="h-64 flex items-center justify-center"><Loader2 className="w-5 h-5 text-[#D4AF37] animate-spin" /></div>
      ) : series.length === 0 ? (
        <div className="h-64 flex items-center justify-center text-sm text-[#525252]">No performance data for this account.</div>
      ) : (
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={series} margin={{ top: 5, right: 5, left: -12, bottom: 0 }}>
            <defs>
              <linearGradient id="pnlFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.25} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#1c1c1c" vertical={false} />
            <XAxis dataKey="date" tick={{ fill: "#525252", fontSize: 10 }} tickLine={false} axisLine={{ stroke: "#262626" }}
              tickFormatter={(v) => v.slice(5)} minTickGap={30} />
            <YAxis tick={{ fill: "#525252", fontSize: 10 }} tickLine={false} axisLine={false}
              tickFormatter={(v) => (mode === "pct" ? `${v}%` : `$${v}`)} width={54} />
            <Tooltip
              contentStyle={{ background: "#0D0D0D", border: "1px solid #262626", borderRadius: 4, fontSize: 12 }}
              labelStyle={{ color: "#A3A3A3" }} itemStyle={{ color }}
              formatter={(v) => [mode === "pct" ? `${v}%` : `$${v}`, "Cum PNL"]} />
            <Area type="monotone" dataKey="value" stroke={color} strokeWidth={1.75} fill="url(#pnlFill)" />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

function PnlHistoryTable({ id, base = "/portfolio" }) {
  const [page, setPage] = useState(1);
  const { data, isLoading } = useQuery({
    queryKey: ["pnl-hist", base, id, page],
    queryFn: async () => (await api.get(`${base}/${id}/pnl-history`, { params: { page, size: PAGE_SIZE, days: 90 } })).data,
  });
  const items = data?.items || [];
  const total = data?.total || 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const exportCsv = async () => {
    try { await downloadCsv(`${base}/${id}/pnl-history.csv?days=90`, `pnl-history-${id}.csv`); toast.success("CSV downloaded"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="rounded-md border border-[#262626] bg-[#141414]" data-testid="pnl-history-table">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#262626]">
        <span className="font-heading font-semibold text-[#F5F5F5] text-sm">PNL History</span>
        <button onClick={exportCsv} data-testid="pnl-export-csv" className="flex items-center gap-1.5 text-xs text-[#A3A3A3] hover:text-[#D4AF37] transition-colors">
          <Download className="w-3.5 h-3.5" /> Export CSV
        </button>
      </div>
      {isLoading ? (
        <div className="h-40 flex items-center justify-center"><Loader2 className="w-5 h-5 text-[#D4AF37] animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="h-40 flex items-center justify-center text-sm text-[#525252]">No history available.</div>
      ) : (
        <>
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#262626] text-left">
                <th className="text-[10px] uppercase tracking-wider text-[#737373] font-medium px-4 py-2.5">Date</th>
                <th className="text-[10px] uppercase tracking-wider text-[#737373] font-medium px-4 py-2.5 text-right">PNL ($)</th>
                <th className="text-[10px] uppercase tracking-wider text-[#737373] font-medium px-4 py-2.5 text-right">PNL (%)</th>
                <th className="text-[10px] uppercase tracking-wider text-[#737373] font-medium px-4 py-2.5 text-right">Cum ($)</th>
                <th className="text-[10px] uppercase tracking-wider text-[#737373] font-medium px-4 py-2.5 text-right">Cum (%)</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.date} className="border-b border-[#1c1c1c] last:border-0 hover:bg-[#1A1A1A]">
                  <td className="px-4 py-2.5 font-mono text-xs text-[#A3A3A3]">{it.date}</td>
                  <td className={cn("px-4 py-2.5 text-right font-mono text-sm", pnlColor(it.pnl_usd))}>{fmtUsd(it.pnl_usd)}</td>
                  <td className={cn("px-4 py-2.5 text-right font-mono text-sm", pnlColor(it.pnl_pct))}>{fmtPct(it.pnl_pct)}</td>
                  <td className={cn("px-4 py-2.5 text-right font-mono text-sm", pnlColor(it.cum_pnl_usd))}>{fmtUsd(it.cum_pnl_usd)}</td>
                  <td className={cn("px-4 py-2.5 text-right font-mono text-sm", pnlColor(it.cum_pnl_pct))}>{fmtPct(it.cum_pnl_pct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-center justify-between px-4 py-3 border-t border-[#262626]">
            <span className="text-xs text-[#737373]">Page {page} of {pages} · {total} rows</span>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} data-testid="pnl-prev-page"
                className="p-1.5 rounded-sm border border-[#333333] text-[#A3A3A3] disabled:opacity-40 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"><ChevronLeft className="w-4 h-4" /></button>
              <button onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page >= pages} data-testid="pnl-next-page"
                className="p-1.5 rounded-sm border border-[#333333] text-[#A3A3A3] disabled:opacity-40 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function OrdersTab({ id, base = "/portfolio" }) {
  const [kind, setKind] = useState("positions");
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["orders", base, id, kind],
    queryFn: async () => (await api.get(`${base}/${id}/orders`, { params: { kind } })).data,
  });

  const exportCsv = async () => {
    try { await downloadCsv(`${base}/${id}/orders.csv?kind=${kind}`, `orders-${kind}-${id}.csv`); toast.success("CSV downloaded"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="rounded-md border border-[#262626] bg-[#141414]" data-testid="orders-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#262626] flex-wrap gap-2">
        <div className="flex items-center bg-[#0A0A0A] border border-[#333333] rounded-sm p-0.5">
          {[["positions", "Positions"], ["open", "Open Orders"], ["history", "History"]].map(([k, l]) => (
            <button key={k} onClick={() => setKind(k)} data-testid={`orders-kind-${k}`}
              className={cn("px-3 py-1.5 text-xs rounded-sm transition-colors", kind === k ? "bg-[#D4AF37] text-[#0A0A0A] font-medium" : "text-[#A3A3A3] hover:text-[#F5F5F5]")}>{l}</button>
          ))}
        </div>
        <button onClick={exportCsv} data-testid="orders-export-csv" className="flex items-center gap-1.5 text-xs text-[#A3A3A3] hover:text-[#D4AF37] transition-colors">
          <Download className="w-3.5 h-3.5" /> Export CSV
        </button>
      </div>
      {isLoading ? (
        <div className="h-40 flex items-center justify-center"><Loader2 className="w-5 h-5 text-[#D4AF37] animate-spin" /></div>
      ) : rows.length === 0 ? (
        <div className="h-40 flex items-center justify-center text-sm text-[#525252]">No {kind === "open" ? "open orders" : kind}.</div>
      ) : kind === "history" ? (
        <table className="w-full">
          <thead><tr className="border-b border-[#262626] text-left">
            {["Symbol", "Side", "Type", "Price", "Qty", "Status", "Time"].map((h, i) => (
              <th key={h} className={cn("text-[10px] uppercase tracking-wider text-[#737373] font-medium px-4 py-2.5", i >= 3 && i <= 4 && "text-right")}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-[#1c1c1c] last:border-0 hover:bg-[#1A1A1A]">
                <td className="px-4 py-2.5 font-mono text-sm text-[#F5F5F5]">{r.symbol}</td>
                <td className={cn("px-4 py-2.5 text-sm", r.side === "LONG" ? "text-[#10B981]" : "text-[#EF4444]")}>{r.side}</td>
                <td className="px-4 py-2.5 text-sm text-[#A3A3A3]">{r.type}</td>
                <td className="px-4 py-2.5 text-right font-mono text-sm text-[#F5F5F5]">{fmtNum(r.price)}</td>
                <td className="px-4 py-2.5 text-right font-mono text-sm text-[#A3A3A3]">{r.qty}</td>
                <td className="px-4 py-2.5 text-xs text-[#10B981]">{r.status}</td>
                <td className="px-4 py-2.5 font-mono text-xs text-[#737373]">{r.time}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <table className="w-full">
          <thead><tr className="border-b border-[#262626] text-left">
            {["Symbol", "Side", "Lev", "Size", "Margin", "Entry", "Mark", "PNL"].map((h, i) => (
              <th key={h} className={cn("text-[10px] uppercase tracking-wider text-[#737373] font-medium px-4 py-2.5", i >= 2 && "text-right")}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-[#1c1c1c] last:border-0 hover:bg-[#1A1A1A]">
                <td className="px-4 py-2.5 font-mono text-sm text-[#F5F5F5]">{r.symbol}</td>
                <td className={cn("px-4 py-2.5 text-sm", r.side === "LONG" ? "text-[#10B981]" : "text-[#EF4444]")}>{r.side}</td>
                <td className="px-4 py-2.5 text-right font-mono text-sm text-[#A3A3A3]">{r.leverage}x</td>
                <td className="px-4 py-2.5 text-right font-mono text-sm text-[#A3A3A3]">{r.size}</td>
                <td className="px-4 py-2.5 text-right font-mono text-sm text-[#A3A3A3]">{fmtNum(r.margin)}</td>
                <td className="px-4 py-2.5 text-right font-mono text-sm text-[#F5F5F5]">{fmtNum(r.entry_price)}</td>
                <td className="px-4 py-2.5 text-right font-mono text-sm text-[#F5F5F5]">{fmtNum(r.mark_price)}</td>
                <td className={cn("px-4 py-2.5 text-right font-mono text-sm", pnlColor(r.pnl))}>{fmtNum(r.pnl)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function AllocationCard({ id, base = "/portfolio" }) {
  const { data } = useQuery({ queryKey: ["allocation", base, id], queryFn: async () => (await api.get(`${base}/${id}/allocation`)).data });
  const assets = data?.assets || [];
  return (
    <div className="rounded-md border border-[#262626] bg-[#141414]" data-testid="allocation-card">
      <div className="px-4 py-3 border-b border-[#262626] font-heading font-semibold text-[#F5F5F5] text-sm">Asset Allocation</div>
      {assets.length === 0 ? (
        <div className="h-32 flex items-center justify-center text-sm text-[#525252]">No assets.</div>
      ) : assets.map((a) => (
        <div key={a.asset} className="px-4 py-3 border-b border-[#1c1c1c] last:border-0">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-sm text-[#F5F5F5] font-medium">{a.asset}</span>
            <span className="font-mono text-sm text-[#A3A3A3]">{fmtUsd(a.usd_value)}</span>
          </div>
          <div className="h-1.5 bg-[#0A0A0A] rounded-full overflow-hidden">
            <div className="h-full bg-[#D4AF37]" style={{ width: `${a.share}%` }} />
          </div>
          <div className="flex items-center justify-between mt-1 text-[11px] text-[#737373] font-mono">
            <span>{a.amount} {a.asset}</span><span>{a.share}%</span>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function StatsReports({ base = "/portfolio", backPath = "/portfolio", partner = false }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [syncing, setSyncing] = useState(false);

  const { data, isLoading } = useQuery({ queryKey: ["pf-stats", base, id], queryFn: async () => (await api.get(`${base}/${id}/stats`)).data });

  const sync = async () => {
    setSyncing(true);
    try {
      const res = await api.post(`${base}/${id}/sync`);
      qc.invalidateQueries({ queryKey: ["pf-stats", base, id] });
      res.data.ok ? toast.success("Account synced") : toast.warning(res.data.message);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSyncing(false); }
  };

  if (isLoading || !data) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" /></div>;

  const acc = data.account;
  const s = data.stats;
  const ex = getExchange(acc.exchange_slug);
  const displayStatus = acc.connection_status === "unverified" ? "unverified" : acc.status;

  return (
    <div>
      <button onClick={() => navigate(backPath)} data-testid="stats-back-btn" className="flex items-center gap-2 text-sm text-[#A3A3A3] hover:text-[#F5F5F5] mb-4 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back to Portfolio Tracker
      </button>

      <PageHeader
        title={acc.account_name}
        subtitle={`${ex.name} · Stats & Reports`}
        pageKey="stats-reports"
        testid="stats-header"
        actions={!partner &&
          <button onClick={sync} disabled={syncing} data-testid="account-sync-btn"
            className={cn("flex items-center gap-2 text-sm rounded-sm px-4 py-2 transition-colors disabled:opacity-60",
              displayStatus === "unverified"
                ? "bg-[#D4AF37] hover:bg-[#F0C74B] text-[#0A0A0A] font-semibold"
                : "border border-[#333333] hover:border-[#D4AF37] hover:text-[#D4AF37] text-[#F5F5F5]")}>
            <RefreshCw className={cn("w-4 h-4", syncing && "animate-spin")} /> {displayStatus === "unverified" ? "Live Sync" : "Sync"}
          </button>
        }
      />

      <div className="flex items-center gap-2 mb-5">
        {partner ? (
          <>
            <span className="text-[10px] uppercase tracking-wide text-[#A3A3A3] bg-[#1A1A1A] border border-[#262626] px-2 py-0.5 rounded-sm">{acc.connected ? "Connected" : "Disconnected"}</span>
            <span className="text-[10px] uppercase tracking-wide text-[#A3A3A3] bg-[#1A1A1A] border border-[#262626] px-2 py-0.5 rounded-sm capitalize">{acc.market}</span>
          </>
        ) : (
          <><RoleBadge role={acc.account_role} /> <StatusBadge status={displayStatus} /></>
        )}
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="bg-[#0D0D0D] border border-[#262626] rounded-sm p-1 h-auto">
          <TabsTrigger value="overview" data-testid="tab-overview" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0A] text-[#A3A3A3] rounded-sm px-4 py-1.5 text-sm">Overview</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0A] text-[#A3A3A3] rounded-sm px-4 py-1.5 text-sm">Orders</TabsTrigger>
          <TabsTrigger value="portfolio" data-testid="tab-portfolio" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0A] text-[#A3A3A3] rounded-sm px-4 py-1.5 text-sm">Portfolio</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-5 space-y-5">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            {/* Left column: Balance / AUM / Return + Copiers */}
            <div className="lg:col-span-1 space-y-4">
              <div className="rounded-md border border-[#262626] bg-[#141414] p-5" data-testid="stat-balance">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#A3A3A3]">Balance</span>
                  <Wallet className="w-4 h-4 text-[#737373]" strokeWidth={1.75} />
                </div>
                <div className="font-heading text-3xl font-bold text-[#F5F5F5] mt-3 tabular">{fmtUsd(s.balance)}</div>
              </div>

              <div className="rounded-md border border-[#262626] bg-[#141414] p-5" data-testid="stat-aum">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#A3A3A3]">AUM</span>
                  <Coins className="w-4 h-4 text-[#737373]" strokeWidth={1.75} />
                </div>
                <div className="font-heading text-3xl font-bold text-[#F5F5F5] mt-3 tabular">{fmtUsd(s.aum || 0)}</div>
                <div className="text-xs text-[#737373] mt-1">Assets under management</div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-md border border-[#262626] bg-[#141414] p-5" data-testid="stat-return">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[#A3A3A3]">Return</span>
                    <Undo2 className="w-4 h-4 text-[#737373]" strokeWidth={1.75} />
                  </div>
                  <div className={cn("font-heading text-2xl font-bold mt-3 tabular", pnlColor(s.return))}>{s.return != null ? fmtPct(s.return) : "—"}</div>
                  <div className="text-xs text-[#737373] mt-1">Total return</div>
                </div>
                <div className="rounded-md border border-[#262626] bg-[#141414] p-5" data-testid="stat-copiers">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[#A3A3A3]">Copiers</span>
                    <Users className="w-4 h-4 text-[#737373]" strokeWidth={1.75} />
                  </div>
                  <div className="font-heading text-2xl font-bold text-[#F5F5F5] mt-3 tabular">{s.copiers ?? 0}</div>
                  <div className="text-xs text-[#737373] mt-1">Active copiers</div>
                </div>
              </div>
            </div>

            {/* Middle column: Positions */}
            <div className="lg:col-span-2 rounded-md border border-[#262626] bg-[#141414] flex flex-col" data-testid="positions-card">
              <div className="flex items-center gap-2 px-5 py-4">
                <SlidersHorizontal className="w-4 h-4 text-[#D4AF37]" />
                <span className="font-heading font-semibold text-[#F5F5F5]">Positions</span>
              </div>
              <div className="px-5 pb-3 flex items-center gap-1.5 text-xs text-[#737373]">
                Updated: {acc.last_sync_at || acc.updated_at ? fmtDate(acc.last_sync_at || acc.updated_at) : "—"} <Info className="w-3 h-3" />
              </div>
              <div className="px-4 pb-4 flex-1">
                <div className="rounded-md border border-[#1f1f1f] overflow-hidden h-full min-h-[280px] flex flex-col">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#262626] text-left bg-[#0D0D0D]">
                        {["Size", "Symbol", "Entry Price", "PNL", "Liq. Price"].map((h, i) => (
                          <th key={h} className={cn("text-[11px] uppercase tracking-wider text-[#737373] font-medium px-4 py-3", i > 1 && "text-right")}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    {s.positions && s.positions.length > 0 && (
                      <tbody>
                        {s.positions.map((p, i) => (
                          <tr key={i} className="border-b border-[#1c1c1c] last:border-0 hover:bg-[#1A1A1A]">
                            <td className={cn("px-4 py-2.5 font-mono text-sm", p.size >= 0 ? "text-[#10B981]" : "text-[#EF4444]")}>{p.size}</td>
                            <td className="px-4 py-2.5 font-mono text-sm text-[#F5F5F5]">{p.symbol}</td>
                            <td className="px-4 py-2.5 text-right font-mono text-sm text-[#F5F5F5]">{fmtNum(p.entry_price)}</td>
                            <td className={cn("px-4 py-2.5 text-right font-mono text-sm", pnlColor(p.pnl))}>{fmtNum(p.pnl)}</td>
                            <td className="px-4 py-2.5 text-right font-mono text-sm text-[#A3A3A3]">{fmtNum(p.liq_price)}</td>
                          </tr>
                        ))}
                      </tbody>
                    )}
                  </table>
                  {(!s.positions || s.positions.length === 0) && (
                    <div className="flex-1 flex items-center justify-center text-sm text-[#525252]" data-testid="positions-empty">No data...</div>
                  )}
                </div>
              </div>
            </div>

            {/* Right column: Stats */}
            <div className="lg:col-span-1 rounded-md border border-[#262626] bg-[#141414] p-5" data-testid="stats-card">
              <div className="flex items-center gap-2 mb-5">
                <List className="w-4 h-4 text-[#D4AF37]" />
                <span className="font-heading font-semibold text-[#F5F5F5]">Stats</span>
              </div>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-[#A3A3A3]">Avg. Return</span>
                    <span className="inline-flex items-center gap-1 text-[11px] text-[#F5F5F5] border border-[#333333] rounded-sm px-1.5 py-0.5">1M <ChevronDown className="w-3 h-3 text-[#737373]" /></span>
                  </div>
                  <span className={cn("font-mono text-sm font-semibold", pnlColor(s.avg_return_1m))} data-testid="stat-avg-1m">{s.avg_return_1m != null ? fmtPct(s.avg_return_1m) : "—"}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#A3A3A3]">Active days</span>
                  <span className="font-mono text-sm text-[#F5F5F5]" data-testid="stat-active-days">{s.active_days ?? "—"}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#A3A3A3]">Drawdown</span>
                  <span className="font-mono text-sm text-[#F5F5F5]" data-testid="stat-drawdown">{s.drawdown != null ? `${s.drawdown}%` : "—"}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#A3A3A3]">Avg. Weekly Trades</span>
                  <span className="font-mono text-sm text-[#F5F5F5]" data-testid="stat-weekly-trades">{s.avg_weekly_trades ?? 0}</span>
                </div>
                <div className="h-px bg-[#1f1f1f] my-1" />
                <div>
                  <div className="text-sm text-[#A3A3A3] mb-2.5">Most Traded Assets</div>
                  <div className="flex flex-wrap gap-2" data-testid="stat-most-traded">
                    {(s.most_traded || []).length === 0 ? (
                      <span className="text-sm text-[#525252]">—</span>
                    ) : s.most_traded.map((a) => (
                      <span key={a} className="text-xs font-medium text-[#F5F5F5] bg-[#1A1A1A] border border-[#262626] px-2.5 py-1 rounded-full">{a}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <PositionsHeatmap positions={s.positions} />

          <PnlChart id={id} base={base} />

          <PnlHistoryTable id={id} base={base} />
        </TabsContent>

        <TabsContent value="orders" className="mt-5"><OrdersTab id={id} base={base} /></TabsContent>

        <TabsContent value="portfolio" className="mt-5 space-y-5">
          <div className="grid lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2"><PnlChart id={id} base={base} /></div>
            <AllocationCard id={id} base={base} />
          </div>
          <PnlHistoryTable id={id} base={base} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Layers2Fallback(props) { return <TrendingUp {...props} />; }
